import { useState, useEffect } from "react";
import Table from 'react-bootstrap/Table';
import { Button, Row, Col, Form } from "react-bootstrap";
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

function Invitations() {
  const { searchInvitableUsers } = useAuth();
  const [tfcbtUsers, setTfcbtUsers] = useState([]);
  const [searchData, setSearchData] = useState({ search_type: "email", search_term: "" });
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [invitingId, setInvitingId] = useState(null);

  const getUserKey = (u) => `${u.user_type}-${u.id}`;

  const searchTerm = searchData.search_term?.trim() || "";

  useEffect(() => {
    if (searchTerm === "") {
      setTfcbtUsers([]);
      setLoading(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setSearchError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await searchInvitableUsers(searchData.search_type, searchTerm);
        if (cancelled) return;

        if (response?.success) {
          setTfcbtUsers(response.invitable_users || []);
        } else {
          setTfcbtUsers([]);
          setSearchError(response?.message || "Search failed. Please try again.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Search error:", err);
          setTfcbtUsers([]);
          setSearchError("Network error conducting search.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchData.search_type, searchData.search_term]);

  const inviteUser = async (user) => {
    const key = getUserKey(user);
    try {
      setInvitingId(key);
      const response = await fetch(`${API_URL}/api/v1/admin_dashboard/${user.id}/invite_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_type: user.user_type })
      });

      const data = await response.json();

      if (response.ok) {
        setTfcbtUsers(prev => prev.filter(u => getUserKey(u) !== key));
      } else {
        alert(`Invitation failed: ${data.errors?.join(", ") || "Please try again."}`);
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      alert("Network error inviting user.");
    } finally {
      setInvitingId(null);
    }
  };

  const handleChange = (e) => {
    setSearchData({ ...searchData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <Row className="mx-4 mt-5">
        <h2>Invite Learners</h2>
        <Form>
          <Form.Group as={Row}>
            <Col md={2} className="ml-0 pl-0">
              <FloatingLabel label="Search By">
                <Form.Select
                  name="search_type"
                  value={searchData.search_type || "email"}
                  onChange={handleChange}
                >
                  <option value="email">Email</option>
                  <option value="last_name">Last Name</option>
                  <option value="first_name">First Name</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col md={5}>
              <FloatingLabel label="Search">
                <Form.Control
                  name="search_term"
                  value={searchData.search_term || ""}
                  onChange={handleChange}
                  placeholder="Search by email, first or last name..."
                />
              </FloatingLabel>
            </Col>
          </Form.Group>
        </Form>
      </Row>
      {/* State 1) Search box empty */}
      {searchTerm === "" ? (
        <div className="text-center text-muted my-5 py-5">
          <i className="fas fa-search fa-2x mb-3 text-secondary"></i>
          <h5>The search returns TF-CBT (English or Spanish) accounts that completed TF-CBT but have not yet been invited or logged in to CTG2.</h5>
          </div>
      ) : loading ? (
        /* State 2) Searching */
        <div className="text-center text-muted my-5 py-5">
          <h5>Searching learners...</h5>
        </div>
      ) : searchError ? (
        /* State 3) Search Error */
        <div className="alert alert-danger mx-4 my-4" role="alert">
          {searchError}

        </div>
      ) : tfcbtUsers.length === 0 ? (
        /* State 4) No results */
        <div className="text-center text-muted my-5 py-5">
          <h5>No matches found</h5>
          <p className="mb-0">No uninvited learners matching "{searchTerm}" who completed TFCBT were found.</p>
          </div>
      ) : (
        /* State 5) Results found -> render table */
        <>
          <Table
            striped
            hover
            style={{
              backgroundColor: "#dee8faff",
              marginTop: "30px",
              marginLeft: "20px",
              marginRight: "20px"
            }}
            >
              <thead>
                <tr>
                  <th>Source App</th>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tfcbtUsers.map(tfcbtUser => {
                  const key = getUserKey(tfcbtUser);
                  const isSpanish = tfcbtUser.user_type === "TfcbtEs::User";
                  return (
                    <tr key={key}>
                    <td>
                      {isSpanish ? "Spanish" : "English"}
                    </td>
                    <td>{tfcbtUser.email}</td>
                    <td>{tfcbtUser.tfcbt_profile?.first_name || "-"}</td>
                    <td>{tfcbtUser.tfcbt_profile?.last_name || "-"}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        disabled={invitingId === key}
                        onClick={() => inviteUser(tfcbtUser)}
                        >
                          {invitingId === key ? "Inviting..." : "Invite User"}
                        </Button>
                        </td>
                        </tr>
                  );
                })}
              </tbody>
            </Table>

            {tfcbtUsers.length >= 50 && (
              <p className="text-muted text-center mt-2">
                Showing first 50 matches across English and Spanish users tables.
              </p>
            )}
        </>
      )}
    </div>
  )
}

export default Invitations;
